import { useMemo } from 'react';
import { KfsInputs, KfsCalculatedValues } from '../types/kfs.types';
import { calculateAll } from '../utils/kfsFormulas';

export interface ValidationErrors {
  loanAmount?: string;
  loanDate?: string;
  interestRate?: string;
  grossWeight?: string;
}

interface UseKfsCalculatorResult {
  values: KfsCalculatedValues | null;
  isValid: boolean;
  errors: ValidationErrors;
}

function countDecimals(value: number): number {
  const str = String(value);
  const dotIndex = str.indexOf('.');
  if (dotIndex === -1) return 0;
  return str.length - dotIndex - 1;
}

function validateInputs(inputs: KfsInputs): ValidationErrors {
  const errors: ValidationErrors = {};

  if (inputs.loanAmount != null) {
    if (inputs.loanAmount <= 0) {
      errors.loanAmount = 'Loan amount must be greater than 0';
    } else if (countDecimals(inputs.loanAmount) > 2) {
      errors.loanAmount = 'Maximum 2 decimal places allowed';
    } else if (inputs.loanAmount > 9999999999) {
      errors.loanAmount = 'Maximum loan amount is ₹99,99,99,999';
    }
  }

  if (inputs.loanDate != null) {
    const today = new Date();
    today.setHours(23, 59, 59, 999);
    if (inputs.loanDate > today) {
      errors.loanDate = 'Loan date cannot be a future date';
    }
  }

  if (inputs.interestRate != null) {
    if (inputs.interestRate <= 0) {
      errors.interestRate = 'Interest rate must be greater than 0';
    } else if (inputs.interestRate > 100) {
      errors.interestRate = 'Interest rate cannot exceed 100%';
    } else if (countDecimals(inputs.interestRate) > 3) {
      errors.interestRate = 'Maximum 3 decimal places allowed';
    }
  }

  if (inputs.grossWeight != null) {
    if (inputs.grossWeight <= 0) {
      errors.grossWeight = 'Gross weight must be greater than 0';
    } else if (countDecimals(inputs.grossWeight) > 2) {
      errors.grossWeight = 'Maximum 2 decimal places allowed';
    }
  }

  return errors;
}

export function useKfsCalculator(inputs: KfsInputs): UseKfsCalculatorResult {
  return useMemo(() => {
    const errors = validateInputs(inputs);
    const hasErrors = Object.keys(errors).length > 0;
    const allFilled =
      inputs.loanAmount != null &&
      inputs.loanDate != null &&
      inputs.interestRate != null &&
      inputs.grossWeight != null;

    const isValid = allFilled && !hasErrors;
    const values = isValid ? calculateAll(inputs) : null;

    return { values, isValid, errors };
  }, [inputs]);
}
